import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Modal, Field, TextInput, Textarea, PrimaryButton, useFetch } from "./ui";
import { Plus, Trash2 } from "lucide-react";

/**
 * Shared "Close Installation · Generate Installed Base" modal.
 * Pulls the linked Opportunity's Sales Quote line items, lets the SPOC / Service
 * Incharge validate quantity and key serial numbers per unit, and add extra
 * materials — then POSTs /opportunities/{oid}/complete-installation which marks
 * BOTH the Opportunity activity and its mirrored Ticket activity as completed
 * and generates the Installed Base (IB) record.
 *
 * Usable from both entry points:
 *   • Opportunity Detail — Phase 4 "Installation Completion confirmation"
 *   • Ticket Detail — the ticket's mirrored copy of the same activity
 *
 * Props: open, oid (opportunity id — resolved via ticket.linked_opp_id on the
 * ticket side), activityId, customerName, onClose(), onSuccess(data)
 */
export default function CompleteInstallationModal({ open, oid, activityId, customerName, onClose, onSuccess }) {
  const { data: materials } = useFetch("/materials");

  const [installLines, setInstallLines] = useState([]);
  const [installQuoteNumber, setInstallQuoteNumber] = useState(null);
  const [installLinesLoading, setInstallLinesLoading] = useState(false);
  const [installDate, setInstallDate] = useState(new Date().toISOString().slice(0, 10));
  const [installNotes, setInstallNotes] = useState("");
  const [installSaving, setInstallSaving] = useState(false);
  const [existingIb, setExistingIb] = useState(null);
  const [comprehensiveWarrantyDays, setComprehensiveWarrantyDays] = useState("");
  const [systemSizeKw, setSystemSizeKw] = useState("");

  useEffect(() => {
    if (!open || !oid) return;
    setInstallDate(new Date().toISOString().slice(0, 10));
    setInstallNotes("");
    setInstallLines([]);
    setInstallQuoteNumber(null);
    setExistingIb(null);
    setComprehensiveWarrantyDays("");
    setSystemSizeKw("");
    setInstallLinesLoading(true);
    api.get(`/opportunities/${oid}/install-lines`).then(({ data }) => {
      setInstallQuoteNumber(data.quotation_number || null);
      if (data.existing_ib) {
        // Editing a previously-closed installation — prefill from the IB itself
        // (grouped by material) so a correction updates the same record.
        const ib = data.existing_ib;
        setExistingIb(ib);
        setInstallDate((ib.commissioning_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
        setInstallNotes(ib.notes || "");
        setComprehensiveWarrantyDays(ib.comprehensive_warranty_days ?? "");
        setSystemSizeKw(ib.system_size_kw ?? "");
        const groups = [];
        const byMaterial = {};
        for (const eq of ib.equipment || []) {
          if (!byMaterial[eq.material_id]) {
            byMaterial[eq.material_id] = {
              material_id: eq.material_id, material_code: eq.material_code, material_name: eq.material_name,
              description: eq.description, quotation_quantity: null, quantity: 0, serials: [],
              is_extra: eq.is_extra, warranty_days: eq.warranty_days,
            };
            groups.push(byMaterial[eq.material_id]);
          }
          byMaterial[eq.material_id].serials.push(eq.serial_code);
          byMaterial[eq.material_id].quantity += 1;
        }
        setInstallLines(groups);
      } else {
        setInstallLines((data.lines || []).map((l) => ({
          material_id: l.material_id, material_code: l.material_code, material_name: l.material_name,
          description: l.description, quotation_quantity: l.quantity, quantity: l.quantity,
          serials: Array.from({ length: l.quantity }, () => ""), is_extra: false, warranty_days: l.warranty_days,
        })));
      }
    }).catch((err) => {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }).finally(() => setInstallLinesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, oid, activityId]);

  function addExtraLine() {
    setInstallLines((prev) => [...prev, {
      material_id: "", material_code: "", material_name: "", description: "",
      quotation_quantity: null, quantity: 1, serials: [""], is_extra: true, warranty_days: null,
    }]);
  }

  function setLineQuantity(i, qty) {
    const q = Math.max(1, Number(qty) || 1);
    setInstallLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const serials = [...l.serials];
      while (serials.length < q) serials.push("");
      while (serials.length > q) serials.pop();
      return { ...l, quantity: q, serials };
    }));
  }

  function setLineMaterial(i, materialId) {
    const mat = materials.find((m) => m.id === materialId);
    setInstallLines((prev) => prev.map((l, idx) => idx !== i ? l : {
      ...l, material_id: materialId, material_code: mat?.code || "", material_name: mat?.name || "", description: mat?.description || "",
      warranty_days: l.warranty_days ?? mat?.warranty_days ?? null,
    }));
  }

  function setLineWarrantyDays(i, value) {
    setInstallLines((prev) => prev.map((l, idx) => idx !== i ? l : { ...l, warranty_days: value === "" ? null : Math.max(0, Number(value) || 0) }));
  }

  function setLineSerial(i, j, value) {
    setInstallLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const serials = [...l.serials];
      serials[j] = value;
      return { ...l, serials };
    }));
  }

  function removeLine(i) {
    setInstallLines((prev) => prev.filter((_, idx) => idx !== i));
  }

  const suggestedWarrantyDays = Math.max(0, ...installLines.map((l) => l.warranty_days || 0), 0);

  async function submitInstallCompletion(e) {
    e.preventDefault();
    if (!installLines.length) {
      toast.error("Add at least one equipment line");
      return;
    }
    if (installLines.some((l) => !l.material_id)) {
      toast.error("Every line needs a material selected");
      return;
    }
    if (installLines.some((l) => l.serials.some((s) => !s.trim()))) {
      toast.error("Enter the serial number for every unit before confirming");
      return;
    }
    const entries = [];
    for (const l of installLines) {
      for (const s of l.serials) {
        entries.push({ material_id: l.material_id, serial_code: s.trim(), is_extra: l.is_extra, warranty_days: l.warranty_days ?? null });
      }
    }
    setInstallSaving(true);
    try {
      const payload = {
        activity_id: activityId,
        commissioning_date: new Date(installDate).toISOString(),
        notes: installNotes,
        entries,
        comprehensive_warranty_days: comprehensiveWarrantyDays === "" ? null : Number(comprehensiveWarrantyDays),
        system_size_kw: systemSizeKw === "" ? null : Number(systemSizeKw),
      };
      const { data } = await api.post(`/opportunities/${oid}/complete-installation`, payload);
      toast.success(data.action === "updated"
        ? `Installed Base ${data.ib_number} updated · Warranty End Date ${new Date(data.warranty_valid_date).toLocaleDateString()}`
        : `Installation closed · ${data.ib_number} generated · Warranty End Date ${new Date(data.warranty_valid_date).toLocaleDateString()}`);
      onSuccess?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setInstallSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={existingIb ? `Edit Installation · ${existingIb.ib_number}` : "Close Installation · Generate Installed Base"} wide>
      <form onSubmit={submitInstallCompletion} className="space-y-4" data-testid="install-completion-form">
        <p className="text-sm text-[#4A5568]">
          {existingIb ? (
            <>Correcting equipment, serials or warranty here updates <span className="font-semibold text-[#0A1128]">{existingIb.ib_number}</span> in place — it will <span className="font-semibold">not</span> create a duplicate Installed Base record.</>
          ) : (
            <>Marking <span className="font-semibold text-[#0A1128]">"Installation Completion confirmation"</span> as completed will generate an Installed Base (IB) record from the equipment below — validate quantities against the quote, then key in each unit's serial number.</>
          )}
        </p>
        <div className="grid md:grid-cols-5 gap-4">
          <Field label="Commissioning date *">
            <TextInput type="date" required value={installDate} onChange={(e) => setInstallDate(e.target.value)} data-testid="install-date" />
          </Field>
          <Field label={`Comprehensive Warranty (days)${suggestedWarrantyDays ? ` · suggested ${suggestedWarrantyDays}` : ""}`}>
            <TextInput type="number" min={0} value={comprehensiveWarrantyDays}
              onChange={(e) => setComprehensiveWarrantyDays(e.target.value)}
              placeholder={suggestedWarrantyDays ? String(suggestedWarrantyDays) : "e.g. 365"}
              data-testid="install-comprehensive-warranty-days" />
          </Field>
          <Field label="Customer (auto)">
            <TextInput value={customerName || ""} disabled />
          </Field>
          <Field label="Sales Quote (auto)">
            <TextInput value={installQuoteNumber || "No quotation linked"} disabled data-testid="install-quote-number" />
          </Field>
          <Field label="System Size (kW)">
            <TextInput type="number" min={0} step="0.1" value={systemSizeKw}
              onChange={(e) => setSystemSizeKw(e.target.value)}
              placeholder="e.g. 5.5" data-testid="install-system-size-kw" />
          </Field>
        </div>
        <div className="text-[10px] font-mono text-[#94A3B8]">
          Header Warranty End Date = Commissioning date + Comprehensive Warranty (days). Leave blank to auto-use the longest line-item warranty below.
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A5568]">Equipment (validate quantity · enter serials · optional warranty override) *</div>
            <button type="button" onClick={addExtraLine}
              className="text-xs font-mono uppercase tracking-widest text-[#1F4E2C]" data-testid="install-add-line">
              <Plus className="w-3 h-3 inline" /> Add material
            </button>
          </div>

          {installLinesLoading && <div className="text-xs font-mono text-[#94A3B8] py-3">Loading quotation line items…</div>}
          {!installLinesLoading && installLines.length === 0 && (
            <div className="text-xs font-mono text-[#94A3B8] py-3 border border-dashed border-[#DDE5D8] px-3">
              No quotation line items found for this Opportunity. Use "Add material" to register the installed equipment manually.
            </div>
          )}

          <div className="space-y-3">
            {installLines.map((line, i) => (
              <div key={i} className="border border-[#E2E4DC] p-3" data-testid={`install-line-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    {line.is_extra ? (
                      <select value={line.material_id} onChange={(e) => setLineMaterial(i, e.target.value)}
                        data-testid={`install-mat-${i}`}
                        className="w-full border border-[#E2E4DC] px-2 py-1.5 text-sm font-mono">
                        <option value="">— Select material —</option>
                        {materials.map((m) => <option key={m.id} value={m.id}>{`${m.code} · ${m.name}`}</option>)}
                      </select>
                    ) : (
                      <div>
                        <div className="font-medium text-sm text-[#0F1A12]">{line.material_name}</div>
                        <div className="font-mono text-[10px] text-[#4A5568] uppercase tracking-widest">{line.material_code} {line.description ? `· ${line.description}` : ""}</div>
                      </div>
                    )}
                    {line.is_extra && <div className="font-mono text-[9px] uppercase tracking-widest text-amber-700 mt-1">Extra · not on quote</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">
                        Warranty (days)
                      </div>
                      <input type="number" min={0} value={line.warranty_days ?? ""}
                        onChange={(e) => setLineWarrantyDays(i, e.target.value)}
                        placeholder="Default"
                        data-testid={`install-warranty-${i}`}
                        className="w-20 border border-[#E2E4DC] px-2 py-1 text-sm font-mono text-right" />
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">
                        Qty {line.quotation_quantity != null && `· Quoted ${line.quotation_quantity}`}
                      </div>
                      <input type="number" min={1} value={line.quantity}
                        onChange={(e) => setLineQuantity(i, e.target.value)}
                        data-testid={`install-qty-${i}`}
                        className="w-16 border border-[#E2E4DC] px-2 py-1 text-sm font-mono text-right" />
                    </div>
                    <button type="button" onClick={() => removeLine(i)} className="text-red-600 mt-4">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {line.quotation_quantity != null && line.quantity !== line.quotation_quantity && (
                  <div className="text-[10px] font-mono text-orange-700 mt-1">Differs from quoted quantity — please confirm before closing.</div>
                )}
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                  {line.serials.map((s, j) => (
                    <input key={j} value={s}
                      onChange={(e) => setLineSerial(i, j, e.target.value)}
                      placeholder={`Serial · Unit ${j + 1} *`}
                      data-testid={`install-serial-${i}-${j}`}
                      className="border border-[#E2E4DC] px-2 py-1.5 text-sm font-mono" />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] font-mono text-[#94A3B8] mt-2">
            Leave Warranty blank to use the material master's default. The header's Warranty Valid Date is auto-computed from the longest line-item warranty.
          </div>
        </div>

        <Field label="Notes">
          <TextInput value={installNotes} onChange={(e) => setInstallNotes(e.target.value)}
            placeholder="Any handover notes…" />
        </Field>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">Cancel</button>
          <PrimaryButton type="submit" disabled={installSaving || installLinesLoading} testid="install-complete-submit">
            {installSaving ? "Saving…" : existingIb ? "Update Installation" : "Close & Generate IB"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
